import { HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import { HdChironPosition, HdChironRole } from '../hd-chiron.models';
import { HdAstroPointPosition } from '../hd-astro-points.models';
import { HdGateInfoCheatSheetVisualState } from './variants/hd-gate-info-cheat-sheet.component';

/**
 * Definitie-staat van een poort in de huidige chart — bepaalt welk
 * planeet-invloed-veld (in defined channel / in defined center / in undefined
 * center) inline naast een planeet-activatie getoond wordt.
 *
 *  - `inDefinedChannel`: poort maakt deel uit van een volledig defined kanaal.
 *  - `inDefinedCenter`: poort zit in een defined center maar niet in een defined kanaal.
 *  - `inUndefinedCenter`: poort zit in een undefined center.
 */
export type HdGateDefinitionState = 'inDefinedChannel' | 'inDefinedCenter' | 'inUndefinedCenter';

/**
 * Context aangeleverd aan {@link HdGateInfoRequestedComponent} (variant
 * `chart-detail` van de `hd-gate-info` factory). Beschrijft niet enkel om
 * welke poort het gaat, maar ook vanuit welke klik-bron het panel geopend
 * werd zodat de getoonde activatie-regels daarop kunnen aansluiten.
 */
export interface HdGateInfoRequestedContext {
  readonly gate: number;
  /** Discriminator zodat we deze context kunnen onderscheiden van {@link HdGateInfoContext} in de factory. */
  readonly kind: 'requested';
  /**
   * `'chart-gate'` = klik in de bodygraph.
   * `'planet-grid-block'` = klik op één planet-blokje.
   *
   * Sinds de inhoudsharmonisatie (zie spec
   * `docs/human-design/hd-chart-info-modus-en-floating-dialogs.md` §1.7)
   * leveren **beide bronnen alle planeet-activaties van de poort** op via
   * {@link activations}. De bron-discriminator wordt enkel nog gebruikt om
   * bij `'planet-grid-block'` de geklikte activatie eerst te sorteren en
   * eventueel bron-specifieke labels te kiezen in de regel-helper.
   */
  readonly source: 'chart-gate' | 'planet-grid-block';
  /**
   * Alle planeet-activaties van die poort in de huidige chart.
   *  - `'chart-gate'`: kan leeg zijn (geen planeet-activatie voor die
   *    poort in de chart).
   *  - `'planet-grid-block'`: ≥ 1, met de geklikte activatie op index 0
   *    (sorteervolgorde — zie spec §1.7 en `sortActivationsWithClickedFirst`).
   */
  readonly activations: readonly HdBodygraphPlanetaryActivation[];
  /**
   * Alle planet-activaties in de huidige chart (alle poorten). Optioneel: enkel
   * vereist voor varianten die fixaties via harmonische poorten willen detecteren
   * (zie {@link HdGateInfoFullComponent} — exaltation/detriment kan veroorzaakt
   * worden door de activerende planeet in de harmonische poort, de andere kant
   * van het kanaal). Bij gebruik buiten de editor (geen chart-context) blijft dit
   * weg en wordt enkel binnen de poort gekeken.
   */
  readonly allActivations?: readonly HdBodygraphPlanetaryActivation[];
  /**
   * Volledige personality-snapshot van de actieve Just Now-transit. Deze bron
   * blijft apart van {@link allActivations}, zodat de gate-info zowel de
   * oorsprong van een activatie als tijdelijke fixaties via een harmonische
   * transitpoort ondubbelzinnig kan bepalen.
   */
  readonly transitActivations?: readonly HdBodygraphPlanetaryActivation[];
  /**
   * `true` wanneer de poort enkel actief is via de editor (manual fill) en
   * geen planeet-activatie heeft. Enkel relevant voor source `'chart-gate'`.
   */
  readonly isDummyActiveViaEditor: boolean;
  /**
   * Visuele staat voor de embedded cheat-sheet kaart. Wordt door de
   * editor-page bepaald op basis van de huidige render-state:
   *  - `'defined'`: poort heeft activatie en het center is defined.
   *  - `'undefined'`: poort heeft activatie maar het center is undefined.
   *  - `'unactivated'`: poort heeft géén activatie in de chart.
   * Optioneel: bij gebruik buiten de editor (bv. directe `<hd-gate-info>`-aanroep
   * zonder chart-context) blijft de cheat-sheet de default `'defined'`-styling
   * tonen.
   */
  readonly visualState?: HdGateInfoCheatSheetVisualState;
  /**
   * Definitie-staat van de poort in de huidige chart. Drijft welke
   * planeet-invloed-keynote inline onder elke planet-activatie getoond wordt:
   *  - `inDefinedChannel` → veld `inDefinedChannel` van de planeet-invloed.
   *  - `inDefinedCenter`  → veld `inDefinedCenter`.
   *  - `inUndefinedCenter`→ veld `inUndefinedCenter`.
   * Optioneel: bij gebruik buiten de editor (geen render-state beschikbaar,
   * bv. vanuit de transit-popup) blijft het veld weg en toont de gate-info
   * in plaats daarvan alle niet-lege invloed-varianten met label onder elkaar.
   */
  readonly gateDefinitionState?: HdGateDefinitionState;
  /**
   * Definitie-staat van de poort in de VOLLEDIGE transit-chart (natal +
   * transit) — dezelfde combinatie die de bodygraph met transitOverlay
   * tekent. Drijft de planeet-invloed-keynote van enkel de TRANSIT-
   * activaties (just-now): een transit die een kanaal/center voltooit moet
   * de 'defined'-variant tonen, ook wanneer de poort natal in een undefined
   * center ligt. Natal-activaties blijven {@link gateDefinitionState}
   * gebruiken.
   * Optioneel: enkel gezet wanneer de context transit-activaties bevat;
   * bij ontbreken vallen transit-activaties terug op
   * {@link gateDefinitionState} (het oude gedrag).
   */
  readonly transitGateDefinitionState?: HdGateDefinitionState;
  /**
   * Weergavestijl van de transitlaag: 'just-now' (default, integraal groen) of
   * 'cycle' (bron-tinten; activaties behouden hun kant-kleuren).
   */
  readonly transitStyle?: 'just-now' | 'cycle';
  /** Label voor transit-rijen in de panelen; afwezig/null = het Just Now-label. */
  readonly transitRowLabel?: string | null;
  /**
   * Weergavestijl van de basislaag (de eigen chart-activaties): 'standard'
   * (default; design rood, personality zwart) of 'composite' (chart 1 kleurt
   * natal-blauw — twee personen, twee kleuren).
   */
  readonly baseStyle?: 'standard' | 'composite';
  /**
   * Begin-mode van de router-variant (`chart-detail`):
   *  - `'small'` (default) → compacte inhoud (glyph-rij + cheat-sheet);
   *  - `'full'`  → meteen de volledige detail-weergave.
   * De gebruiker kan daarna live wisselen via de toggle-knop; die wijziging
   * blijft lokaal in de component (zie spec §2.2) en raakt deze context niet.
   */
  readonly initialMode?: HdGateInfoMode;
  /**
   * Chiron-positie van deze persoon op deze poort (klik op Chiron in de grid,
   * de statistieken of het basis-paneel). GEEN activatie: telt niet mee in
   * {@link activations}, de cheat-sheet-staat of de definitie. De gate-info
   * toont haar lijn standaard via de lijnfilter (als niet-geactiveerde lijn
   * met een markering) en de Chiron-planeetinfo zonder invloed-keynote.
   */
  readonly chiron?: HdChironPosition;
  /**
   * Rol van {@link chiron} in het wond-verhaal. Ontbreekt = afgeleid uit de zijde
   * (natal: personality = wond, design = werk); `'resolution'` = de design-Chiron
   * van een Chiron return chart (geopend vanuit de cyclusinfo).
   */
  readonly chironRole?: HdChironRole;
  /**
   * Positie van een astrologisch punt (AC, DC, MC, IC, Black Moon Lilith) van deze persoon op deze
   * poort (klik in de astro-rij onder de planetengrid). Zelfde regels als {@link chiron}: GEEN
   * activatie, enkel een gemarkeerde lijn en een informatieve regel met de basis-keynote van het punt.
   */
  readonly astroPoint?: HdAstroPointPosition;
}

/** Inhouds-mode van de gate-info router-variant. */
export type HdGateInfoMode = 'small' | 'full';

/**
 * Standaard open-gedrag van gate-info panels, instelbaar via de controls-bar
 * dropdown:
 *  - `'small'` (default) → elke poort opent als compact floating dialog;
 *  - `'full'` → elke poort opent meteen als volledige detail-dialog (floating);
 *  - `'full-docked-keep'` → elke poort opent meteen full **én** gedockt; nieuwe
 *    panels worden bij de gedockte lijst gevoegd (bestaande blijven staan);
 *  - `'full-docked-autoclose'` → idem, maar het vorige via deze modus
 *    auto-gedockte panel wordt gesloten zodra je een nieuwe poort aanklikt
 *    (handmatig gedockte panels blijven onaangeroerd).
 */
export type HdGateInfoOpenBehavior =
  | 'small'
  | 'full'
  | 'full-docked-keep'
  | 'full-docked-autoclose';

export function isHdGateInfoRequestedContext(
  candidate: { gate: number; kind?: string },
): candidate is HdGateInfoRequestedContext {
  return candidate.kind === 'requested';
}
